import { useLocation } from 'react-router-dom';

import type { Papel } from '@/nucleo/auth/papeis';
import { useAuth } from '@/nucleo/auth/useAuth';

/**
 * Quem está olhando o Fechamento Mensal V2. A tela é uma só para o DTP, a SME
 * e a DRE, como no protótipo (`FechamentoMensalV2DetailPage.tsx`,
 * `useSmeDrePermissoes`); o que muda é o recorte de abas e de ações.
 */
export type PersonaFechamento = 'DTP' | 'SME' | 'DRE';

/** Papéis que enxergam além de uma diretoria — o "escopo global" do protótipo. */
const PAPEIS_ESCOPO_GLOBAL: readonly Papel[] = [
  'ADMIN',
  'GESTOR_ADMIN',
  'GESTOR',
  'OPERADOR',
  'FINANCEIRO',
  'FISCAL',
  'AUDITOR',
  'GESTOR_SME',
  'OPERADOR_SME',
];

/**
 * Mesma regra do protótipo:
 *   - DRE: tem papel de diretoria e nenhum de escopo global;
 *   - SME: papel SME sem Financeiro nem Admin, OU qualquer não-DRE entrando
 *     pela casca `/sme/` — lá dentro até o Admin vê a tela como a SME vê;
 *   - DTP: o resto.
 */
export function personaDoFechamento(papeis: readonly Papel[], pathname: string): PersonaFechamento {
  const tem = (...p: Papel[]) => p.some((x) => papeis.includes(x));
  const drePuro =
    tem('GESTOR_DRE', 'OPERADOR_DRE') && !PAPEIS_ESCOPO_GLOBAL.some((p) => papeis.includes(p));
  if (drePuro) return 'DRE';
  const smePuro = tem('GESTOR_SME', 'OPERADOR_SME') && !tem('FINANCEIRO', 'ADMIN', 'GESTOR_ADMIN');
  const naCascaSme = pathname === '/sme' || pathname.startsWith('/sme/');
  return smePuro || naCascaSme ? 'SME' : 'DTP';
}

export function usePersonaFechamento(): PersonaFechamento {
  const { usuario } = useAuth();
  const { pathname } = useLocation();
  return personaDoFechamento(usuario?.papeis ?? [], pathname);
}
